import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { api, API } from "modules/api";
import { GetOrganizationsResponse } from "modules/api/endpoints/organizationsGet";
import { OrganizationType } from "modules/api/endpoints/organizationGet";
import {
  PerspectiveType,
  GetPerspectivesResponse,
} from "modules/api/endpoints/perspectivesGet";

import { useAuth } from "modules/hooks/auth";
import { useModal } from "modules/hooks/modal";
import { disableAllAssessmentsContext } from "modules/utils/allAssessmentsContext";

import Template from "./Template";
import MetaTitle from "components/MetaTitle";
import { boolean } from "yup";

const Route: React.FunctionComponent = () => {
  const [organizations, setOrganizations] = useState<OrganizationType[]>([]);
  const [otherUserOrganizations, setOtherUserOrganizations] = useState<
    OrganizationType[]
  >([]);
  const [perspectives, setPerspectives] = useState<PerspectiveType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isReadOnlyAssessments, setIsReadOnlyAssessments] = useState(false);

  const {
    user,
    logout,
    roles,
    selectedOrganizationId,
    organizations: authOrganizations,
  } = useAuth();

  const dialog = useModal();
  const history = useHistory();

  const handleHeadlingTitle = useCallback(() => {
    return `Welcome, ${user.firstName ? user.firstName : "User"}`;
  }, [user]);

  useEffect(() => {
    disableAllAssessmentsContext();

    let isCancelled = false;

    if (window.location.href.includes("readonlyassessments")) {
      setIsReadOnlyAssessments(true);
    } else {
      setIsReadOnlyAssessments(false);
    }

    const fetchOrganizations = async () => {
      try {
        const [organizationsData, perspectivesData] = await Promise.all([
          await api(API.GET_ORGANIZATIONS()).then(
            (organizationsResponse: GetOrganizationsResponse) =>
              organizationsResponse.data
          ),
          api(API.GET_PERSPECTIVES()).then(
            (res: GetPerspectivesResponse) => res.data
          ),
        ]);
        console.log("organizationsData", organizationsData);
        if (!organizationsData.myOrgnizations.length === !isCancelled) {
          history.push("/organization");
          return;
        }
        setPerspectives(perspectivesData);

        // Filter organizations based on selected organization from auth token
        let filteredOrganizations = organizationsData.myOrgnizations;

        // If user has selected an organization, filter to show only that organization
        if (
          selectedOrganizationId &&
          authOrganizations &&
          authOrganizations.length > 0
        ) {
          // Find the selected organization from auth token
          const selectedAuthOrg = authOrganizations.find(
            (org) => org.organizationId === selectedOrganizationId
          );

          if (selectedAuthOrg) {
            // Filter to show only the organization that matches the selected one
            // Try matching by consultancy.externalId first, then by organization name
            const matchedOrgs = organizationsData.myOrgnizations.filter(
              (org) => {
                // Match by consultancy.externalId if available
                if (org.organization.consultancy?.externalId) {
                  return (
                    org.organization.consultancy.externalId ===
                    selectedOrganizationId
                  );
                }
                // Fallback: match by organization name
                return (
                  org.organization.name === selectedAuthOrg.organizationName
                );
              }
            );

            // Only use filtered results if we found at least one match
            // Otherwise, show all organizations as fallback
            if (matchedOrgs.length > 0) {
              filteredOrganizations = matchedOrgs;
            }
          }
        }

        setOrganizations(filteredOrganizations);
        // setOtherUserOrganizations(organizationsData.otherUserOrgnizations);
        setIsLoading(false);
      } catch (error) {
        await dialog({
          type: "error",
          title: "Sorry, your credentials has expired.",
          description: "Please try to login again...",
          catchOnCancel: true,
        }).finally(() => window.location.assign(logout()));
        setIsLoading(false);
      }
    };

    fetchOrganizations();

    return () => {
      isCancelled = true;
    };
  }, [selectedOrganizationId, authOrganizations]);

  return (
    <React.Fragment>
      <MetaTitle title={"Maturity Assessment Tool | PeopleCert"} />
      <Template
        isLoading={isLoading}
        organizations={organizations}
        perspectives={perspectives}
        handleHeadlingTitle={handleHeadlingTitle}
        roles={roles}
      />
      {/* Uncomment the following block if needed */}
      {/*
      {otherUserOrganizations?.length > 0 && isReadOnlyAssessments && (
        <Template
          isLoading={isLoading}
          organizations={otherUserOrganizations}
          perspectives={perspectives}
          handleHeadlingTitle={handleHeadlingTitle}
          roles={roles}
        />
      )}
      */}
    </React.Fragment>
  );
};

export default Route;
